'use client';

import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import { Server, ChevronRight } from 'lucide-react';
import type { NetworkNode } from '@/hooks/useDashboardRealtime';

interface Props {
  nodes: NetworkNode[];
  loading: boolean;
}

const cpuColor = (v: number) =>
  v >= 90 ? 'bg-danger' : v >= 70 ? 'bg-warning' : 'bg-success';

export default function NodeStatusGrid({ nodes, loading }: Props) {
  const [filter, setFilter] = useState<string>('all');

  const filtered = filter === 'all' ? nodes : nodes.filter((n) => n.node_status === filter);

  return (
    <div className="bg-surface border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Server size={16} className="text-primary" />
          <p className="text-sm font-semibold text-foreground">Node Status</p>
          <span className="text-[10px] bg-surface-raised border border-border px-2 py-0.5 rounded-full text-muted font-mono">
            {loading ? '…' : `${nodes.length} nodes`}
          </span>
          {!loading && nodes.length > 0 && (
            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
          )}
        </div>
        <div className="flex items-center gap-1">
          {['all', 'critical', 'degraded', 'recovering', 'healthy'].map((f) => (
            <button
              key={`filter-node-${f}`}
              onClick={() => setFilter(f)}
              className={`text-[10px] font-semibold px-2 py-1 rounded-md transition-all duration-150 capitalize
                ${filter === f
                  ? 'bg-primary/20 text-primary border border-primary/30' :'text-muted hover:text-foreground hover:bg-surface-raised'
                }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading && nodes.length === 0 ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-9 bg-surface-raised rounded animate-pulse" />
            ))}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                {['Node', 'Region', 'Type', 'Status', 'CPU', 'Mem', 'BGP Peers', 'Last Event', ''].map((h) => (
                  <th key={`th-node-${h}`} className="text-left pb-2 pr-3 text-[10px] font-semibold uppercase tracking-widest text-muted whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((node) => (
                <tr
                  key={node.id}
                  className="border-b border-border/40 hover:bg-surface-raised/50 transition-colors group"
                >
                  <td className="py-2.5 pr-3 font-mono text-[11px] text-foreground font-medium whitespace-nowrap">
                    {node.name}
                  </td>
                  <td className="py-2.5 pr-3 text-muted whitespace-nowrap">{node.region}</td>
                  <td className="py-2.5 pr-3 text-muted whitespace-nowrap">{node.node_type}</td>
                  <td className="py-2.5 pr-3">
                    <StatusBadge status={node.node_status} />
                  </td>
                  <td className="py-2.5 pr-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-12 h-1.5 bg-surface-overlay rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${cpuColor(node.cpu)} transition-all`}
                          style={{ width: `${node.cpu}%` }}
                        />
                      </div>
                      <span className="font-mono text-[10px] text-muted">{node.cpu}%</span>
                    </div>
                  </td>
                  <td className="py-2.5 pr-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-12 h-1.5 bg-surface-overlay rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${cpuColor(node.memory)} transition-all`}
                          style={{ width: `${node.memory}%` }}
                        />
                      </div>
                      <span className="font-mono text-[10px] text-muted">{node.memory}%</span>
                    </div>
                  </td>
                  <td className="py-2.5 pr-3 font-mono text-[11px] text-foreground-muted">{node.bgp_peers}</td>
                  <td className="py-2.5 pr-3 text-muted whitespace-nowrap">{node.last_event}</td>
                  <td className="py-2.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ChevronRight size={12} className="text-muted" />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={9} className="py-6 text-center text-muted text-xs">No nodes match filter</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}